import { useState } from "react";
import { toast } from "sonner";
import { Eye, PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import CountryPicker from "@/components/admin/CountryPicker";
import GenderSplit, { genderLabel } from "@/components/admin/launch/GenderSplit";
import { api } from "@/lib/api";
import { countryName } from "@/lib/countries";
import { formatDate } from "@/lib/format";

const EMPTY = { country: "", state: "", city: "", zip: "", count: "50", balance_gender: true };
const toItems = (split = {}) => {
  const n = Object.values(split).reduce((a, b) => a + b, 0) || 1;
  return Object.entries(split).sort((a, b) => b[1] - a[1]).map(([gender, count]) => ({ gender, count, pct: Math.round((1000 * count) / n) / 10 }));
};
const place = (w) => [w.zip, w.city, w.state, w.country && countryName(w.country)].filter(Boolean).join(" · ");

function Preview({ p }) {
  if (!p.count) return <p className="cw-tone-warning mt-5 rounded-xl px-4 py-3 text-sm" role="status" data-testid="release-preview-nobody">Nobody matches.</p>;
  return (
    <div className="mt-5 space-y-4" data-testid="release-preview">
      <p className="text-sm"><span className="font-semibold tabular-nums" data-testid="release-preview-count">{p.count}</span> of <span className="tabular-nums" data-testid="release-preview-matching">{p.matching}</span> waiting people match{p.count < p.requested ? ` (fewer than the ${p.requested} asked)` : ""}.</p>
      <GenderSplit items={toItems(p.split)} testId="release-preview-split" />
      {p.preview?.length > 0 && (
        <div className="max-h-72 overflow-auto rounded-xl border border-border">
          <table className="w-full text-left text-sm" data-testid="release-preview-table">
            <thead><tr className="cw-muted border-b border-border text-xs"><th className="px-4 py-2 font-medium">Email</th><th className="py-2 pr-4 font-medium">Gender</th><th className="py-2 pr-4 font-medium">Area</th><th className="py-2 pr-4 font-medium">Joined</th></tr></thead>
            <tbody>{p.preview.map((w) => (
              <tr key={w.id} className="border-b border-border last:border-0" data-testid={`release-preview-row-${w.id}`}>
                <td className="px-4 py-2">{w.email}</td><td className="py-2 pr-4">{genderLabel(w.gender)}</td><td className="cw-soft py-2 pr-4">{place(w)}</td><td className="cw-muted whitespace-nowrap py-2 pr-4">{formatDate(w.created_at)}</td>
              </tr>))}</tbody>
          </table>
          {p.count > p.preview.length && <p className="cw-muted px-4 py-2 text-xs">Showing the first {p.preview.length}.</p>}
        </div>
      )}
    </div>
  );
}

function Done({ r }) {
  return (
    <div className="cw-tone-success mt-5 space-y-2 rounded-xl px-4 py-3 text-sm" role="status" data-testid="release-result">
      <p className="font-medium"><span data-testid="release-result-count">{r.released}</span> {r.released === 1 ? "person" : "people"} released. Batch <span className="font-mono" data-testid="release-result-batch">{r.batch_id}</span>.</p>
      <p className="text-foreground">{toItems(r.split).map((g) => `${genderLabel(g.gender)} ${g.count}`).join(" · ")}</p>
      <p className="cw-soft">Each gets a “you’re in” notice by push, then text, then email. Notices due in quiet hours wait until they end.</p>
    </div>
  );
}

function AreaFields({ f, set }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <div><Label htmlFor="rb-country" className="text-xs">Country</Label>
        <CountryPicker id="rb-country" value={f.country} onChange={set("country")} anyLabel="Any country" className="mt-1" testId="release-country-picker" /></div>
      <div><Label htmlFor="rb-state" className="text-xs">State / province</Label><Input id="rb-state" value={f.state} onChange={set("state")} placeholder="Any" className="mt-1 bg-white" data-testid="release-state-input" /></div>
      <div><Label htmlFor="rb-city" className="text-xs">City</Label><Input id="rb-city" value={f.city} onChange={set("city")} placeholder="Any" className="mt-1 bg-white" data-testid="release-city-input" /></div>
      <div><Label htmlFor="rb-zip" className="text-xs">Postal codes</Label><Input id="rb-zip" value={f.zip} onChange={set("zip")} placeholder="53202, K1A 0B6" className="mt-1 bg-white" data-testid="release-zip-input" /></div>
    </div>
  );
}

// Owner only. Preview = dry_run:true; release = the same filter with dry_run:false, behind a confirm.
export default function ReleaseBatch({ onReleased }) {
  const [f, setF] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const set = (k) => (e) => { setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e })); setPreview(null); setResult(null); setError(null); };
  const count = Number(f.count);
  const validCount = Number.isInteger(count) && count >= 1 && count <= 5000;
  const body = (dry_run) => ({ country: f.country || null, state: f.state.trim() || null, city: f.city.trim() || null, zip: f.zip.trim() || null, count, balance_gender: f.balance_gender, dry_run });
  const message = (e) => (e.code === "nobody_to_release" ? "Nobody matches." : e.message);

  const runPreview = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null); setResult(null);
    try { setPreview(await api("/admin/waitlist/release", { body: body(true) })); } catch (err) { setError(message(err)); setPreview(null); } finally { setBusy(false); }
  };
  const release = async () => {
    try {
      const r = await api("/admin/waitlist/release", { body: body(false) });
      setResult(r); setPreview(null);
      toast.success(`${r.released} released.`);
      onReleased();
    } catch (err) {
      if (err.code === "nobody_to_release") { setError("Nobody matches."); setPreview(null); return; }
      throw err;
    }
  };

  return (
    <Panel className="p-6" data-testid="release-batch-panel">
      <h2 className="font-semibold">Release a batch</h2>
      <p className="cw-soft mt-1 text-sm">Let waiting people in by area, oldest first. Leave a field empty for “any”.</p>
      <form onSubmit={runPreview} className="mt-4 space-y-4">
        <AreaFields f={f} set={set} />
        <div className="flex flex-wrap items-end gap-5">
          <div><Label htmlFor="rb-count" className="text-xs">How many</Label>
            <Input id="rb-count" type="number" min={1} max={5000} value={f.count} onChange={set("count")} aria-invalid={!validCount} className="mt-1 w-32 bg-white tabular-nums" data-testid="release-count-input" /></div>
          <div className="flex items-center gap-3 pb-2"><Switch id="rb-balance" checked={f.balance_gender} onCheckedChange={set("balance_gender")} data-testid="release-balance-switch" />
            <Label htmlFor="rb-balance">Keep gender balanced</Label></div>
          <div className="ml-auto flex gap-2">
            <Button type="submit" variant="outline" disabled={busy || !validCount} data-testid="release-preview-button"><Eye />{busy ? "Checking…" : "Preview"}</Button>
            <Button type="button" disabled={!preview?.count} onClick={() => setConfirming(true)} data-testid="release-submit-button"><PaperPlaneTilt />{preview?.count ? `Release ${preview.count} ${preview.count === 1 ? "person" : "people"}` : "Release"}</Button>
          </div>
        </div>
        {!validCount && <p className="cw-error text-xs" data-testid="release-count-error">Enter a number from 1 to 5,000.</p>}
      </form>
      {error && <p className="cw-tone-warning mt-4 rounded-xl px-4 py-3 text-sm" role="alert" data-testid="release-error">{error}</p>}
      {preview && <Preview p={preview} />}
      {result && <Done r={result} />}
      <ConfirmDialog open={confirming} onOpenChange={setConfirming} destructive={false} testId="release-confirm-dialog" onConfirm={release}
        title={`Release ${preview?.count} ${preview?.count === 1 ? "person" : "people"}?`} confirmLabel="Release"
        description="They can sign up right away and get a “you’re in” notice. This can’t be undone.">
        {preview && <Pill className="w-fit">{toItems(preview.split).map((g) => `${genderLabel(g.gender)} ${g.count}`).join(" · ")}</Pill>}
      </ConfirmDialog>
    </Panel>
  );
}
